import { useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import { useT } from '../i18n';

interface Props {
  onSubmit: (password: string) => Promise<void>;
}

const MIN_PASSWORD = 8;

// ANX-102 — установка нового пароля по токену из письма. Токен зашит в
// onSubmit-замыкание route-компонентом. not_found = ссылка истекла/неверна.
export function ResetPasswordView({ onSubmit }: Props) {
  const t = useT();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  // expired = токен недействителен/истёк: показываем ссылку «запросить новую».
  const [expired, setExpired] = useState(false);

  const canSubmit = !pending && password !== '' && confirm !== '';

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    if (password.length < MIN_PASSWORD) {
      setError(t('auth.reset.tooShort'));
      return;
    }
    if (password !== confirm) {
      setError(t('auth.reset.mismatch'));
      return;
    }
    setPending(true);
    try {
      await onSubmit(password);
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'not_found') {
        setExpired(true);
      } else if (err instanceof ApiError) {
        setError(
          err.code === 'network'
            ? t('auth.error.network')
            : `${t('common.errorPrefix')}: ${err.message}`,
        );
      } else {
        setError(t('common.unknownError'));
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="bg-white p-6 rounded shadow-sm border border-slate-200 w-full max-w-sm">
        <h1 className="text-xl font-semibold mb-2">{t('auth.reset.title')}</h1>
        {done ? (
          <>
            <p className="text-sm text-slate-700 mb-4">{t('auth.reset.success')}</p>
            <a href="/login" className="text-sm text-accent-600 hover:text-accent-700">
              {t('auth.reset.toLogin')}
            </a>
          </>
        ) : expired ? (
          <>
            <p role="alert" className="text-sm text-red-600 mb-4">
              {t('auth.reset.invalidToken')}
            </p>
            <a
              href="/forgot-password"
              className="text-sm text-accent-600 hover:text-accent-700"
            >
              {t('auth.reset.requestNew')}
            </a>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-600 mb-4">{t('auth.reset.subtitle')}</p>
            <form onSubmit={handleSubmit} className="flex flex-col gap-3 w-full">
              <label className="flex flex-col gap-1">
                <span className="text-sm text-slate-700">{t('auth.reset.password')}</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="border border-slate-300 rounded px-3 py-2"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm text-slate-700">{t('auth.reset.confirm')}</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="border border-slate-300 rounded px-3 py-2"
                />
              </label>
              {error !== null && (
                <p role="alert" className="text-sm text-red-600">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={!canSubmit}
                className="bg-slate-900 text-white rounded px-3 py-2 disabled:opacity-50"
              >
                {pending ? t('auth.reset.submitting') : t('auth.reset.submit')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
